import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import logoIcon from "statics/images/biadi-logo-icon.png";
import TIcon from "../../components/ui/TIcon";
import * as SBService from "../../helpers/sportbookService";
import { getAssetFullUrl } from "../../helpers/utils";
import {
  NEWS_TYPE_EVENT,
  NEWS_TYPE_LABELS,
  NEWS_TYPE_NEWS,
  NewsArticleInterface,
  NewsTypeValue,
} from "../../interfaces/news";

const PAGE_SIZE = 10;
const TABS: NewsTypeValue[] = [NEWS_TYPE_NEWS, NEWS_TYPE_EVENT];

/**
 * Màn danh sách Tin tức / Sự kiện.
 *
 * Trang công khai: nằm ngoài Layout nên đọc được khi chưa đăng nhập, và link chia
 * sẻ ra ngoài không bị đá về màn đăng nhập. Vì thế phải tự dựng vỏ .app-shell -
 * Layout là nơi bình thường lo việc đó. Thêm .no-tabbar vì màn này không có thanh
 * tab dưới để chừa chỗ.
 *
 * Khối đang xem nằm trên URL (?type=event) chứ không chỉ trong state: gửi link
 * "Sự kiện" cho nhau thì người nhận phải mở đúng tab đó.
 */
const NewsListingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const typeParam = searchParams.get("type");
  const activeType: NewsTypeValue =
    typeParam === NEWS_TYPE_EVENT ? NEWS_TYPE_EVENT : NEWS_TYPE_NEWS;

  const [articles, setArticles] = useState<NewsArticleInterface[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (type: NewsTypeValue, nextPage: number) => {
    setLoading(true);
    setFailed(false);
    try {
      const rsp = await SBService.getPublicNews({
        type,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      const data: NewsArticleInterface[] = rsp?.data ?? [];

      // Trang 1 là thay cả danh sách (đổi tab), từ trang 2 mới là nối thêm.
      setArticles((current) => (nextPage === 1 ? data : current.concat(data)));
      setHasNextPage(Boolean(rsp?.hasNextPage));
      setPage(nextPage);
    } catch (e: any) {
      setFailed(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = "Biadi — " + NEWS_TYPE_LABELS[activeType];
    load(activeType, 1);
  }, [activeType, load]);

  const switchTab = (type: NewsTypeValue) => {
    if (type === activeType) {
      return;
    }
    setArticles([]);
    setSearchParams(type === NEWS_TYPE_NEWS ? {} : { type });
  };

  return (
    <div className="main app-shell no-tabbar px-5 relative top-0">
      <header className="page-head bleed" style={{ justifyContent: "flex-start", gap: 10 }}>
        <button type="button" className="icon-btn" onClick={() => navigate(-1)}>
          <TIcon name="chevron-left" />
        </button>
        <h1>{NEWS_TYPE_LABELS[activeType].toUpperCase()}</h1>
      </header>

      <div className="seg">
        {TABS.map((type) => (
          <button
            key={`news-tab-${type}`}
            type="button"
            className={`seg-item${type === activeType ? " active" : ""}`}
            onClick={() => switchTab(type)}
          >
            {NEWS_TYPE_LABELS[type]}
          </button>
        ))}
      </div>

      <div style={{ paddingTop: 18 }}>
        {articles.map((article) => (
          <ArticleCard
            key={`news-${article.id}`}
            article={article}
            onOpen={() => navigate(`/news/${article.slug}`)}
          />
        ))}
      </div>

      {!loading && articles.length === 0 ? (
        <div className="ui-card empty-state" style={{ marginTop: 22 }}>
          <TIcon name={failed ? "alert-triangle" : "news"} />
          <div className="t">
            {failed
              ? "Không tải được nội dung"
              : `Chưa có ${NEWS_TYPE_LABELS[activeType].toLowerCase()}`}
          </div>
          <div className="h">
            {failed
              ? "Kiểm tra kết nối rồi thử lại."
              : "Nội dung mới sẽ hiện ở đây."}
          </div>
          {failed ? (
            <button
              type="button"
              className="chip active"
              style={{ marginTop: 12 }}
              onClick={() => load(activeType, 1)}
            >
              Thử lại
            </button>
          ) : null}
        </div>
      ) : null}

      {hasNextPage ? (
        <button
          type="button"
          className="seg-item"
          style={{ width: "100%", marginTop: 8 }}
          disabled={loading}
          onClick={() => load(activeType, page + 1)}
        >
          {loading ? "Đang tải..." : "Xem thêm"}
        </button>
      ) : null}
    </div>
  );
};

/** Một bài trong danh sách. Ảnh trống thì thay bằng logo mờ, xem .thumb-fallback. */
export const ArticleCard = ({
  article,
  onOpen,
}: {
  article: NewsArticleInterface;
  onOpen: () => void;
}) => (
  <button type="button" className="article-card" onClick={onOpen}>
    <div className="thumb">
      {article.image ? (
        <img src={getAssetFullUrl(article.image)} alt="" />
      ) : (
        <span className="thumb-fallback">
          <img src={logoIcon} alt="" />
        </span>
      )}
    </div>
    <div className="info">
      <span className="title">{article.title}</span>
      <span className="meta">
        {[
          article.category,
          article.publishedAt
            ? moment(article.publishedAt).format("DD/MM/YYYY")
            : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </span>
      {article.excerpt ? (
        <span className="excerpt">{article.excerpt}</span>
      ) : null}
    </div>
  </button>
);

export default NewsListingPage;
